import { DataFlow } from '../DataFlow';
import type {
  AlgorithmExtension,
  ExtensionInfo,
  IDataFlowGetter,
  IDataFlowSerialization,
  IDataFlowSetter,
  ITypedValueJsonIO,
  OnChangedCallbackT,
  OnChangedForCallbackT,
  OnFailedCallback,
  AncestryInfo,
  ValueConstructor,
  ValuesCollection,
  VertexInfo
} from '../DataFlow/DataFlow';
import type { IValue } from '../DataFlow/Graph';
import type { CallbackSubscription } from '../Utilities/CallbackManager';
import { CallbackManager } from '../Utilities/CallbackManager';
import { DataFlowSnapshot } from './DataFlowSnapshot';
//import * as DT from './DataTypes';
import { UnknownNameError } from '../DataFlow/DataFlowBuilder';
import type { Type } from '../DataFlow/Type';

export class DataFlowWithSnapshots implements IDataFlowGetter, IDataFlowSetter, IDataFlowSerialization {
  private readonly _dataFlow = new DataFlow();

  private readonly _snapshots = new Map<string, DataFlowSnapshot>();

  private _dataFlowGetter: IDataFlowGetter;

  private readonly _onChangedCallbackManagerMap = new Map<string, CallbackManager<OnChangedForCallbackT<IValue>>>();

  private readonly _onChangedCallbackManager = new CallbackManager<OnChangedCallbackT<IValue>>();

  private readonly _onFailedCallbackManager = new CallbackManager<OnFailedCallback>();

  public constructor() {
    this._dataFlow.addOnChangedCallback((name: string, value: IValue | undefined): void => {
      this._onChangedCallbackManager.invoke(name, value);
      this._onChangedCallbackManagerMap.get(name)?.invoke(value);
    });

    this._dataFlow.addOnFailedCallback((namesList: Array<string>): void => {
      this._onFailedCallbackManager.invoke(namesList);
    });

    this._dataFlowGetter = this._dataFlow;
  }

  public async invalidateSnapshots(): Promise<void> {
    if (this._snapshots.size == 0) {
      return;
    }
    const values: ValuesCollection = [];
    for (const name of this._dataFlowGetter.getInputs()) {
      const value = this._dataFlowGetter.getIValue(name);
      values.push([name, value]);
    }
    await this._dataFlow.setValuesAsync(values, false);
    this._dataFlowGetter = this._dataFlow;
    for (const ss of this._snapshots.values()) {
      ss.dispose();
    }
    this._snapshots.clear();
  }

  // ---- Self private interface

  private _notifyAll(names?: Array<string>): void {
    const namesToNotify = names ? this._dataFlowGetter.getOutputsOf(names) : this._dataFlowGetter.getAll();
    for (const name of namesToNotify) {
      //if (this._dataFlowGetter.getType(name) != DT.Mesh.Type) continue;
      //if (this._dataFlowGetter.getType(name) == DT.Spline.Type) continue;
      const value = this._dataFlowGetter.getIValue(name);
      this._onChangedCallbackManager.invoke(name, value);
      this._onChangedCallbackManagerMap.get(name)?.invoke(value);
    }
  }

  // ---- Self public interface

  public async switchAsync(
    stateIdFrom: string,
    stateIdTo: string,
    values: ValuesCollection,
    shouldNotify = true,
    shouldMemoise = true
  ): Promise<void> {
    if (shouldMemoise) {
      const currSnapshot = this._snapshots.get(stateIdFrom);
      if (!currSnapshot) {
        this._snapshots.set(stateIdFrom, new DataFlowSnapshot(this._dataFlow));
      }
    }
    const snapshot = this._snapshots.get(stateIdTo);
    if (snapshot) {
      this._dataFlowGetter = snapshot;
      if (shouldNotify) {
        this._notifyAll(values.map((namedValue) => namedValue[0]));
      }
    } else {
      this._dataFlowGetter = this._dataFlow;
      await this._dataFlow.setValuesAsync(values, shouldNotify);
    }
  }

  public addOnChangedCallbackFor<T extends IValue>(name: string, func: OnChangedForCallbackT<T>): CallbackSubscription {
    if (!this.has(name)) {
      throw new UnknownNameError(name);
    }
    let mgr = this._onChangedCallbackManagerMap.get(name);
    if (!mgr) {
      mgr = new CallbackManager<OnChangedForCallbackT<IValue>>();
      this._onChangedCallbackManagerMap.set(name, mgr);
    }
    return mgr.subscribe((value: unknown): void => {
      func(value as T);
    });
  }

  public addOnChangedCallback<T extends IValue>(func: OnChangedCallbackT<T>): CallbackSubscription {
    return this._onChangedCallbackManager.subscribe((name: string, value: unknown): void => {
      func(name, value as T);
    });
  }

  public addOnFailedCallback(func: OnFailedCallback): CallbackSubscription {
    return this._onFailedCallbackManager.subscribe(func);
  }

  // ----- IDataFlowGetter

  public getOutputsOf(names: Array<string>): Array<string> {
    return this._dataFlowGetter.getOutputsOf(names);
  }

  public getInputsFor(names: Array<string>): Array<string> {
    return this._dataFlowGetter.getInputsFor(names);
  }

  public getIValue(name: string): IValue | undefined {
    return this._dataFlowGetter.getIValue(name);
  }

  public getValue<T extends IValue>(name: string, constructor: ValueConstructor<T>): T | undefined {
    return this._dataFlowGetter.getValue<T>(name, constructor);
  }

  public getValueAs<T, U extends IValue & T = IValue & T>(
    name: string,
    constructor: ValueConstructor<U>
  ): T | undefined {
    return this._dataFlowGetter.getValue<U>(name, constructor);
  }

  public getType(name: string): Type {
    return this._dataFlowGetter.getType(name);
  }

  public has(name: string): boolean {
    return this._dataFlowGetter.has(name);
  }

  public getInputs(): Array<string> {
    return this._dataFlowGetter.getInputs();
  }

  public getOutputs(): Array<string> {
    return this._dataFlowGetter.getOutputs();
  }

  public getAll(): Array<string> {
    return this._dataFlowGetter.getAll();
  }

  public hasExtension(extensionDescriptor: string): boolean {
    return this._dataFlowGetter.hasExtension(extensionDescriptor);
  }

  public getAncestorsOf(name: string, calculatorNames: Set<string>): Map<number, AncestryInfo> {
    return this._dataFlowGetter.getAncestorsOf(name, calculatorNames);
  }

  // ----- IDataFlowSetter

  public async extendAsync(
    extension: AlgorithmExtension,
    extensionDescriptor?: string,
    shouldNotify = false
  ): Promise<void> {
    await this.invalidateSnapshots();
    await this._dataFlow.extendAsync(extension, extensionDescriptor, shouldNotify);
  }

  public async dropExtensionAsync(extensionDescriptor: string): Promise<void> {
    await this.invalidateSnapshots();
    await this._dataFlow.dropExtensionAsync(extensionDescriptor);
  }

  public async setValuesAsync(values: ValuesCollection, shouldNotify = true): Promise<void> {
    await this.invalidateSnapshots();
    await this._dataFlow.setValuesAsync(values, shouldNotify);
  }

  // ----- IDataFlowSerialization

  public exportVertexInfo(io: ITypedValueJsonIO): Array<VertexInfo> {
    return this._dataFlow.exportVertexInfo(io);
  }

  public importVertexInfo(io: ITypedValueJsonIO, info: Array<VertexInfo>): Promise<void> {
    return this._dataFlow.importVertexInfo(io, info);
  }

  public exportExtensionInfo(): Array<ExtensionInfo> {
    return this._dataFlow.exportExtensionInfo();
  }

  public importExtensionInfo(info: Array<ExtensionInfo>): void {
    return this._dataFlow.importExtensionInfo(info);
  }
}
